import { Spine, Skin } from "@esotericsoftware/spine-pixi-v7";
import * as PIXI from "pixi.js";
import anime from "animejs";
import {
  setShowFreeSpinIntro,
  setShowFreeSpinsReload,
  setFreeSpinsReloadPopupPending,
  setShowTransitionDialog,
  setCriticalAnimationPlaying,
  setUserInterfaceDisabled,
} from "../../../../State/actions";
import { Settings } from "../../../../settings";
import { PixiSettings } from "../../../../pixiSettings";
import { soundManager } from "../../../../SoundManager";
import { SOUND_NAME } from "../../../../sounds";
import TickerTimeout from "../../../../utils/TickerTimeout";
import { FEATURES_CONFIG } from "../../../../featuresConfig";
import SpineAtlasUploader from "../../../../utils/SpineAtlasUploader";
import { FONT_STYLES, scaleStyle } from "../../../../fontStyles";
// import { FS_NUMBER_FONT_SIZE } from "../../../../Games/Main/GambleScatter/GambleLevelOverlay";
import attachTapToContinue from "../../../../utils/attachTapToContinue";
// Temporarily disabled — spine centered at screen center
// import { GAME_CONTAINER_OFFSET_CONFIG } from "../../../../Games/Main/GameContainer/GameContainer";

// ============================================================================
// Sizing — the WinAnimationSecondary baseline, value for value.
//
// The popup scales with the board, and this is a per-device tweak ON TOP of
// that. All 1.0: natural size until proven otherwise. Mobile portrait is the
// one likely to need trimming.
//
// `offset` is a multiple of symbolHeight, pushed UP from the screen centre.
// The old table also carried buttonYOffsetMultiplier and buttonFontScale;
// both are gone, because the button now rides its own bone.
// ============================================================================
// `buttonYOffset` lifts the tap word, in SPINE UNITS. Negative is up.
//
// Every popup puts the word at the SAME screen position, so opening two at
// once shows them perfectly aligned. Total win sets that position: its bone
// sits at -480.24 and lifts 43.66, landing on spine y -436.58 — the offset
// that gives it the same gap from the value that the xbet has.
//
// This spine's tap bone is at -471.38, so it needs -34.80 to reach the same line.
/**
 * How long the popup waits for a tap before closing itself, ms. GDD §12.
 *
 * A ceiling, not a pace: the tap is still the normal way out and cancels this.
 * It exists so a bonus that is triggered and then left — the player walked
 * away, or an autoplay session is running unattended — cannot strand the round
 * behind a popup for ever.
 *
 * THE REAL INTRO ONLY. The reload variant never arms it — see _armAutoHide.
 */
const AUTO_HIDE_MS = 10000;

const DEVICE_MULTIPLIERS_MOBILE_SMALL_PORTRAIT = {
  // scale: 1.0,
  scale: 0.8, // 0.9 everywhere to start — the new art is big (2026-09-30) // reload / retrigger: the H&W intro table (2026-10-01)
  offset: 0.0,
  buttonYOffset: 0.0,
};

const DEVICE_MULTIPLIERS_MOBILE_SMALL_LANDSCAPE = {
  // scale: 1.0,
  scale: 0.65, // 0.9 everywhere to start — the new art is big (2026-09-30) // reload / retrigger: the H&W intro table (2026-10-01)
  offset: 0.0,
  buttonYOffset: -34.8,
};

const DEVICE_MULTIPLIERS_MOBILE_PORTRAIT = {
  // scale: 1.0,
  scale: 0.8, // 0.9 everywhere to start — the new art is big (2026-09-30) // reload / retrigger: the H&W intro table (2026-10-01)
  offset: 0.0,
  buttonYOffset: 0.0,
};

const DEVICE_MULTIPLIERS_MOBILE_LANDSCAPE = {
  // Natural size runs too large on a standard landscape phone.
  // scale: 0.9,
  scale: 0.65, // 0.9 everywhere to start — the new art is big (2026-09-30) // reload / retrigger: the H&W intro table (2026-10-01)
  offset: 0.0,
  buttonYOffset: -34.8,
};

const DEVICE_MULTIPLIERS_TABLET_PORTRAIT = {
  // scale: 1.0,
  scale: 0.9, // 0.9 everywhere to start — the new art is big (2026-09-30) // reload / retrigger: the H&W intro table (2026-10-01)
  offset: 0.0,
  buttonYOffset: 0.0,
};

const DEVICE_MULTIPLIERS_TABLET_LANDSCAPE = {
  // scale: 1.0,
  scale: 0.9, // 0.9 everywhere to start — the new art is big (2026-09-30) // reload / retrigger: the H&W intro table (2026-10-01)
  offset: 0.0,
  buttonYOffset: 0.0,
};

const DEVICE_MULTIPLIERS_DESKTOP = {
  // Desktop takes the landscape-phone treatment: same trim, same tap-word lift.
  // scale: 0.9,
  scale: 0.65, // 0.9 everywhere to start — the new art is big (2026-09-30) // reload / retrigger: the H&W intro table (2026-10-01)
  offset: 0.0,
  buttonYOffset: -34.8,
};

// const SPINE_NAME = "free_spin_intro_dialog";
// The animator's new popup (2026-09-30), shared with the Hold & Win intro and
// the retrigger: YOU HAVE / [n in the eye] / FREE SPINS LEFT —
// txt_slot_you_have, txt_slot_spins_respins_amount, txt_slot_spins_respins,
// txt_slot_taptostart. No level skins. Its tap bone is a hand edit (0, -471.38),
// so the tap line sits where every other popup's does. Only the numbers and
// the wording differ between the three.
// const SPINE_NAME = "hold_and_win_intro";
// Renamed `feature_spins` (2026-10-01): the reload / retrigger popup, not an
// intro — the intros are `feature_intro` (FeatureIntroController).
const SPINE_NAME = "feature_spins";

// Share of the screen width a plate word may use — see _fitPlateTexts.
const PLATE_TEXT_MAX_SCREEN_SHARE = 0.9;

// Every name the skeleton exposes, as candidate LISTS. Zeus's shared `dialogs`
// spine prefixed its animations `free_spin_intro_*` and ran the words together
// in `txt_slot_taptocontinue`; the Mad Labs spine uses the short names. Keeping
// both means a re-export cannot silently resolve to nothing.
const ANIM = {
  IN: ["in", "free_spin_intro_in"],
  IDLE: ["idle", "free_spin_intro_idle"],
  OUT: ["out", "free_spin_intro_out"],
};

// const BUTTON_SLOTS = ["txt_slot_tap_to_continue", "txt_slot_taptocontinue"];
const BUTTON_SLOTS = ["txt_slot_taptostart", "txt_slot_tap_to_continue", "txt_slot_taptocontinue"];
const CONGRATS_SLOTS = ["txt_slot_congratulations", "txt_slot_congrats"]; // none on the new spine
// const YOU_WON_SLOTS = ["txt_slot_you_won", "txt_slot_youwon"];
// const FREESPINS_SLOTS = ["txt_slot_freespins", "txt_slot_free_games"];
const YOU_WON_SLOTS = ["txt_slot_you_have", "txt_slot_you_won", "txt_slot_youwon"];
const FREESPINS_SLOTS = ["txt_slot_spins_respins", "txt_slot_freespins", "txt_slot_free_games"];

// The new spine's text sizes, spine units — the Hold & Win intro's, value for
// value (same art). The lines had 84 on the Mad Labs plate.
const LINE_FONT_SIZE = 140;
const NUMBER_FONT_SIZE = 240;
// Its text slots carry no bone length — the widest a line may be.
const LINE_MAX_WIDTH = 1100;

// ── Levels ──
//
// The spine ships one skin per package won, and each carries its own coloured
// frame plus the pips that are LIT for that level — 4 on high, 2 on mid, none
// on low. That matches the gamble tiers exactly (GDD §6), so the tier the
// backend returns picks the skin directly.
//
// Each level also has its own value slot, so the artist can colour the count
// per level. The unused two simply have no attachment and draw nothing.
const LEVEL_SKINS = { low: "levels/low", mid: "levels/mid", high: "levels/high" };
// const LEVEL_VALUE_SLOTS = {
//   low: "txt_slot_value_low",
//   mid: "txt_slot_value_mid",
//   high: "txt_slot_value_high",
// };
// The new spine has ONE number slot, in the eye, whatever the level.
const LEVEL_VALUE_SLOTS = {
  low: "txt_slot_spins_respins_amount",
  mid: "txt_slot_spins_respins_amount",
  high: "txt_slot_spins_respins_amount",
};
const TIER_TO_LEVEL = { 1: "low", 3: "mid", 5: "high" };
const DEFAULT_LEVEL = "high";

// ── The spin count is the SAME CARD as the one inside the gamble scatter ──
//
// The reveal spins a strip of these cards inside SC1 and lands on one; this
// popup then shows that same card, larger. So the number has to be the same
// face, not merely a similar one — it was winCelebrationCounter here and
// FONT_STYLES.gambleLevel (Troika) there, which is why they did not match.
//
// `this.level` is already "low" / "mid" / "high", which is exactly how
// gamble_level.json keys its three styles — one per tier, because each tier
// colours its number differently.
//
// Sized as a MULTIPLE of the authored size rather than an absolute, so
// scaleStyle scales the stroke and the shadow by the same factor. The two
// then read as one card at two sizes instead of one card and a bigger number
// with hairline edges. Import the authored size so the two cannot drift.
//
// ── Same RATIO as the SCG card, not the same size ──
//
// The two cards are different sizes and always will be. What must match is the
// number measured AGAINST ITS OWN CARD.
//
// The SCG card is correct as authored and is NOT to be touched — see
// FS_NUMBER_FONT_SIZE. This scale is the only knob for the popup.
//
// ── THE CARD HERE IS THE TEAL FRAME, NOT THE GOLD BODY ──
//
// `gold_bg` is 283 and looks like the card in the JSON, but the popup draws
// `scatter2_bg` (317) as a frame AROUND it, and that outer edge is what the eye
// reads as the card. The SCG card has no separate frame: its `gold_bg` IS the
// whole card, border included, which is why 242 x 1.3 = 314.6 equals the
// skeleton's own bounding box. Dividing by 283 here made the number look right
// on paper and small on screen, twice.
//
//   SCG card    76 x 1.3 bone scale = 98.8  against gold_bg     314.6  -> 0.314
//   this popup  76 x 2.0            = 152   against scatter2_bg 317.0  -> 0.48
//
// ── Deliberately ABOVE the SCG card's ratio ──
//
// 1.31 is the exact ratio match and it read small: the popup fills the screen
// and the SCG card is one cell on a busy reel, so the same proportion does not
// carry at that size. The artist's reference sheet draws the digit at about
// half the card, which is where 2.0 lands.
//
// This is the ONLY knob for the popup. Do not raise FS_NUMBER_FONT_SIZE to move
// it — that is the SCG card's own number and blowing it up broke the reel.
// const VALUE_FONT_SCALE = 2.0;

/**
 * FreeSpinIntroDialogController - Handles free spin intro animation sequence
 * Requires manual user interaction to close (button click or overlay click)
 * Modeled on HoldAndWinIntroController
 */
class FreeSpinIntroDialogController {
  constructor(
    container,
    dimensionsContext,
    dispatch,
    symbolHeight,
    overlayRef,
    t,
    // ── One dialog, two purposes ──
    //
    // "intro"  the package has just been won: CONGRATULATIONS / YOU WON / n /
    //          FREE SPINS, and closing it starts the wipe INTO the room.
    // "reload" a reload landed mid-round: FREE SPINS / n / LEFT, where n is the
    //          spins remaining, and closing it simply hands the UI back — the
    //          room is already restored behind it, exactly as Pot Collection's
    //          reload popup works.
    //
    // Parameterised rather than copied. A second 1,000-line controller would
    // drift from this one the moment either is touched, which is precisely what
    // happened to the bonus board's win controller.
    variant = "intro",
  ) {
    this.variant = variant;
    this.isReload = variant === "reload";
    this.container = container;
    this.dimensionsContext = dimensionsContext;
    this.dispatch = dispatch;
    this.symbolHeight = symbolHeight;
    this.overlayRef = overlayRef;
    this.t = t;

    // Extract dimensions object for backward compatibility
    this.dimensions = dimensionsContext.dimensions;

    // Spine instance
    this.introSpine = null;
    this.isPlaying = false;
    this.initialized = false;
    this.isWaitingForClick = false;

    // User interaction state (matching H&W intro pattern)
    this.canSkipAnimation = false;
    this.isHandlingUserClick = false;
    this.debounceTimeout = null;
    this.skipProtectionTimeout = null;
    this.currentTimeout = null;
    // GDD §12: the popup must not sit there for ever waiting for a tap.
    this.autoHideTimeout = null;

    // Button attached to spine slot
    this.buttonContainer = null;
    this.buttonText = null;

    // Which package was won. Drives the skin, the pips and which value slot
    // the count goes in. Replaced by setTier() once the round supplies it.
    this.level = DEFAULT_LEVEL;

    // Internal caches for performance optimization
    this._configCache = null;
    this._deviceScaleMultiplierCache = null;
    this._availableAnimationsCache = null;
    this._lastDevice = null;
    this._lastOrientation = null;

    this.initializeIntroSpine();
  }

  /**
   * Scale and offset for the current device and orientation.
   * Cached on both, as WinAnimationSecondary caches its own.
   */
  getDeviceMultipliers() {
    const { device, orientation, isMobileSmall } = this.dimensionsContext || {};

    if (
      this._configCache !== null &&
      this._lastDevice === device &&
      this._lastOrientation === orientation &&
      this._lastIsMobileSmall === isMobileSmall
    ) {
      return this._configCache;
    }

    const isPortrait = orientation === PixiSettings.ORIENTATION.PORTRAIT;
    let multipliers;
    if (device === PixiSettings.DEVICE.MOBILE) {
      if (isMobileSmall) {
        multipliers = isPortrait
          ? DEVICE_MULTIPLIERS_MOBILE_SMALL_PORTRAIT
          : DEVICE_MULTIPLIERS_MOBILE_SMALL_LANDSCAPE;
      } else {
        multipliers = isPortrait
          ? DEVICE_MULTIPLIERS_MOBILE_PORTRAIT
          : DEVICE_MULTIPLIERS_MOBILE_LANDSCAPE;
      }
    } else if (device === PixiSettings.DEVICE.TABLET) {
      multipliers =
        orientation === PixiSettings.ORIENTATION.PORTRAIT
          ? DEVICE_MULTIPLIERS_TABLET_PORTRAIT
          : DEVICE_MULTIPLIERS_TABLET_LANDSCAPE;
    } else {
      multipliers = DEVICE_MULTIPLIERS_DESKTOP;
    }

    this._configCache = multipliers;
    this._lastDevice = device;
    this._lastOrientation = orientation;
    this._lastIsMobileSmall = isMobileSmall;
    return multipliers;
  }

  /**
   * Apply proportional scaling and centering to spine animation
   * Scales based on symbol size and device type, positions relative to screen center
   */
  applyScaleAndCenter() {
    if (!this.introSpine || !this.dimensions) return;

    const nativeSize = Settings.SYMBOL_HEIGHT;
    const targetSize = this.symbolHeight || nativeSize;
    let scale = Math.max(0.1, Math.min(targetSize / nativeSize, 2));

    const { scale: deviceScaleMultiplier, offset: deviceOffsetMultiplier } =
      this.getDeviceMultipliers();
    scale *= deviceScaleMultiplier;

    this.introSpine.scale.set(scale);

    // The parent container sits at the screen centre, so 0,0 IS dead centre.
    this.introSpine.x = 0;
    this.introSpine.y = -(this.symbolHeight || 0) * deviceOffsetMultiplier;

    // Position comes from the bone. Only the fit has to follow, because the
    // allowance is measured against the spine's current scale.
    this._fitTapButton?.(this.dimensions);
    this._fitPlateTexts();
    this.applyButtonOffset();
  }

  /**
   * Keep the plate's words inside the SCREEN.
   *
   * _addSlotText fits a text to its bone's length, but these bones have no
   * length, so that fit never runs. The congratulation is the widest thing on
   * the popup at 111 — Japanese ("おめでとうございます！") ran off both edges of
   * a phone in portrait — and a label joined from two authored lines (Finnish)
   * can run as wide.
   *
   * The tap word's cap (attachTapToContinue): a share of the screen width,
   * converted into the spine's local space by its scale. The parent container
   * sits unscaled at the screen centre, so the spine's own scale IS its screen
   * scale. Never scales up. Re-run on every resize and text change.
   */
  _fitPlateTexts() {
    if (!this.dimensions?.width) return;
    const spineScale = this.introSpine?.scale?.x || 1;
    const maxWidth =
      (this.dimensions.width * PLATE_TEXT_MAX_SCREEN_SHARE) / spineScale;
    for (const text of [
      this.congratsText,
      this.freeSpinsLeadText,
      this.freeSpinsLabelText,
    ]) {
      if (!text || text.destroyed) continue;
      const limit =
        text._boneWidth > 0 ? Math.min(text._boneWidth, maxWidth) : maxWidth;
      // On the TEXT, never the container: the spine rewrites a slot object's
      // transform every frame (updateSlotObject -> setFromMatrix), so a scale on
      // the container is gone by the next one. See FreeSpinZeroWin._fitSlotText.
      text.scale.set(1);
      const naturalWidth = text.width;
      text.scale.set(naturalWidth > limit ? limit / naturalWidth : 1);
    }
  }

  /**
   * Update dimensions and symbol sizes, then recalculate scale and position
   * OPTIMIZED: Only recalculate when values actually change
   */
  updateDimensions(dimensionsContext, symbolHeight) {
    const oldDimensions = this.dimensionsContext?.dimensions;
    const newDimensions = dimensionsContext?.dimensions;
    const oldDevice = this.dimensionsContext?.device;
    const newDevice = dimensionsContext?.device;

    const dimensionsChanged =
      !oldDimensions ||
      oldDimensions.width !== newDimensions?.width ||
      oldDimensions.height !== newDimensions?.height ||
      this.dimensionsContext?.device !== dimensionsContext?.device ||
      this.dimensionsContext?.orientation !== dimensionsContext?.orientation;

    const symbolHeightChanged = this.symbolHeight !== symbolHeight;
    const deviceChanged = oldDevice !== newDevice;

    // Only proceed if something actually changed
    if (!dimensionsChanged && !symbolHeightChanged) {
      return;
    }

    this.dimensionsContext = dimensionsContext;
    this.dimensions = dimensionsContext.dimensions;
    this.symbolHeight = symbolHeight;

    // Invalidate config cache when device/orientation changes
    if (
      deviceChanged ||
      this._lastDevice !== dimensionsContext.device ||
      this._lastOrientation !== dimensionsContext.orientation
    ) {
      this._configCache = null; // Force getConfig() to recalculate
      this._lastDevice = dimensionsContext.device;
      this._lastOrientation = dimensionsContext.orientation;
    }

    // Update button text and style if device/orientation changed (tap vs click, font size)
    if (deviceChanged && this.buttonText) {
      this.updateButtonText();
    }

    // Always update spine positioning whenever dimensions change
    if (
      this.introSpine &&
      this.dimensions &&
      (dimensionsChanged || symbolHeightChanged)
    ) {
      if (this.symbolHeight && this.symbolHeight > 0) {
        this.applyScaleAndCenter();
      }
    }
  }

  /**
   * Initialize the free spin intro spine
   */
  initializeIntroSpine() {
    try {
      this.introSpine = this.createIntroSpine();

      if (!this.introSpine) {
        return;
      }

      const missingComponents = [];
      if (!this.introSpine.skeleton) missingComponents.push("skeleton");
      if (!this.introSpine.state) missingComponents.push("state");
      if (!this.introSpine.skeleton?.data)
        missingComponents.push("skeleton.data");

      if (missingComponents.length > 0) {
        return;
      }

      this.container.addChild(this.introSpine);
      // ── Hidden at alpha 0, not 0.001 ──
      //
      // 0.001 still RENDERS: PIXI skips a subtree only at `worldAlpha <= 0`. The
      // near-zero was there to keep PIXI's texture GC from unloading this spine's
      // atlas after 60 idle seconds — and that sweep is now off (see Stage.js), so
      // the trick has nothing left to buy. At 0 the whole subtree is skipped, and
      // any fade that raises alpha still brings it back exactly as before.
      this.introSpine.alpha = 0;
      if (this.introSpine) this.introSpine.autoUpdate = false;
      this.introSpine.zIndex = 20;

      this.applyScaleAndCenter();

      if (this.introSpine.parent !== this.container) {
        return;
      }

      this.initialized = true;

      // Invalidate animations cache when spine is reinitialized
      this._availableAnimationsCache = null;

      this.applyLevelSkin();
      this.warmUpIntroSpine();

      this.initializeButton();
      this.initializeTexts();
    } catch (error) {
      // Silent fail
    }
  }

  /**
   * Create intro spine from loaded assets
   */
  createIntroSpine() {
    try {
      const assets = PIXI.Assets.cache;
      const skeletonAlias = `${SPINE_NAME}-skeleton`;
      const atlasAlias = `${SPINE_NAME}-atlas`;

      if (!assets.has(skeletonAlias) || !assets.has(atlasAlias)) {
        return null;
      }

      const introSpine = Spine.from({
        skeleton: skeletonAlias,
        atlas: atlasAlias,
      });

      if (!introSpine?.skeleton?.data?.animations) {
        return null;
      }

      return introSpine;
    } catch (error) {
      return null;
    }
  }

  /** First animation name in `candidates` this skeleton actually has. */
  _resolveAnim(candidates) {
    const animations = this.introSpine?.skeleton?.data?.animations;
    if (!animations) return null;
    for (const name of candidates || []) {
      if (animations.find((a) => a.name === name)) return name;
    }
    return null;
  }

  /**
   * Show the level that was won.
   *
   * `default` holds the machine — background, frame, glows, energy — and the
   * level skin adds only the coloured plate and its pips. They are COMBINED,
   * never swapped: setSkinByName would drop everything in `default` and leave
   * a coloured plate floating on nothing.
   *
   * @param {"low"|"mid"|"high"} level
   */
  applyLevelSkin(level = this.level) {
    if (!this.introSpine?.skeleton) return;
    const data = this.introSpine.skeleton.data;
    const levelSkinName = LEVEL_SKINS[level] || LEVEL_SKINS[DEFAULT_LEVEL];

    const base = data.findSkin("default");
    const levelSkin = data.findSkin(levelSkinName);
    if (!levelSkin) {
      // The new spine (hold_and_win_intro) has no level plates at all — its
      // `default` is the whole picture. Nothing to combine, nothing to warn.
      // console.warn("[FreeSpinIntro] Missing level skin:", levelSkinName);
      return;
    }

    const combined = new Skin("free_spin_intro_combined");
    if (base) combined.addSkin(base);
    combined.addSkin(levelSkin);

    this.introSpine.skeleton.setSkin(combined);
    // Slots only — setToSetupPose would reset BONES and snap the pose.
    this.introSpine.skeleton.setSlotsToSetupPose();
  }

  /**
   * Set the package won, from the gamble's `[spinCount, tier]` pair.
   * Rebuilds the count text, because each level writes into its own slot.
   */
  setTier(tier) {
    const level = TIER_TO_LEVEL[tier] || DEFAULT_LEVEL;
    if (level === this.level) return;
    this.level = level;
    this.applyLevelSkin(level);
    this.initializeValueText();
  }

  /**
   * Initialize skeleton by setting an animation to populate spine structure
   */
  warmUpIntroSpine() {
    if (!this.introSpine?.state) return;

    try {
      const animations = this.getAvailableAnimations();
      if (animations.length > 0) {
        if (this.introSpine) this.introSpine.autoUpdate = true;
        this.introSpine.state.setAnimation(0, animations[0], false);
        this.introSpine.state.update(0.016);
        // Spine 4.2 REQUIRES an argument here. The bare call threw, and the
        // catch below swallowed it — so the warm-up silently did nothing and
        // the first show posed cold.
        if (this.introSpine.skeleton.physics !== undefined) {
          this.introSpine.skeleton.updateWorldTransform(2);
        } else {
          this.introSpine.skeleton.updateWorldTransform(0);
        }
      }
      this.introSpine.alpha = 0;
      if (this.introSpine) this.introSpine.autoUpdate = false;
      // Force the atlas onto the GPU now, so the first show cannot hitch.
      SpineAtlasUploader.upload(SPINE_NAME);
    } catch {
      // Optimisation only.
    }
  }

  /**
   * Get available animations from the spine
   * Cached for performance - invalidated when spine is reinitialized
   */
  getAvailableAnimations() {
    if (!this.introSpine?.skeleton?.data?.animations) return [];

    // Check cache validity
    if (this._availableAnimationsCache !== null) {
      return this._availableAnimationsCache;
    }

    // Calculate and cache
    const animations = this.introSpine.skeleton.data.animations.map(
      (a) => a.name,
    );
    this._availableAnimationsCache = animations;

    return animations;
  }

  /**
   * Tap-to-continue, through the shared builder.
   *
   * Every popup in the game routes through attachTapToContinue, so the word is
   * the same size, sits in the same place relative to its bone and takes the
   * same hit area on all of them. The previous version built a loose container
   * and positioned it by hand off `dimensions.height / 2`, which is exactly the
   * drift that helper exists to prevent.
   */
  initializeButton() {
    const built = attachTapToContinue({
      spine: this.introSpine,
      slotNames: BUTTON_SLOTS,
      label: this._buttonLabel(),
      name: "free_spin_intro_continue_attached_to_slot",
      // The ONLY pointer on the popup. The backdrop dismisses too, but it is
      // not a button and stays on the default cursor — the Zeus popups all
      // pin theirs that way. Routed through handleUserClick so the button and
      // the backdrop share one path, skip protection and debounce included.
      onClick: () => {
        if (this.handleUserClick()) {
          soundManager.play(SOUND_NAME.tap_to_continue);
        }
      },
    });
    if (!built) return;

    this.buttonContainer = built.container;
    this.buttonText = built.text;
    this._fitTapButton = built.fit;
    this._revealMs = built.revealMs;
    this._setTapOffsetY = built.setOffsetY;
    // Revealed by showButton once skip protection lifts.
    this.buttonContainer.alpha = 0;
    if (this.buttonContainer) this.buttonContainer.autoUpdate = false;
    this._fitTapButton(this.dimensions);
    this.applyButtonOffset();
  }

  /**
   * Lift the tap word off its bone, in SPINE UNITS.
   *
   * On the TEXT, because the spine rewrites the container's transform every
   * frame. Spine units rather than a multiple of symbolHeight: text.y is
   * spine-local, so the spine's own scale carries it, and the value can be
   * read straight off the bone geometry. The old form reduced to
   * `offset x SYMBOL_HEIGHT / deviceScale` anyway — the same constant, just
   * two indirections away from the numbers it had to match.
   */
  applyButtonOffset() {
    if (!this._setTapOffsetY) return;
    const { buttonYOffset = 0 } = this.getDeviceMultipliers();
    this._setTapOffsetY(buttonYOffset);
  }

  /** First slot name in `candidates` this skeleton actually has. */
  _resolveSlot(candidates) {
    const skeleton = this.introSpine?.skeleton;
    if (!skeleton) return null;
    for (const name of candidates || []) {
      if (skeleton.findSlot(name)) return name;
    }
    return null;
  }

  /**
   * TAP TO START — the same word the game's own intro popup uses.
   *
   * START rather than CONTINUE because this screen is the same statement that
   * one makes: here is what you have, begin. And TAP on every device — see the
   * note in FreeSpinOutroDialogController for why nothing here says CLICK.
   */
  _buttonLabel() {
    return this.t("tap_to_start").toUpperCase();
  }

  /**
   * Build every text slot.
   *
   * One PIXI.Text per slot, styled from a fontStyles JSON through scaleStyle.
   * Sizes are the artist's authored ones, so scaleStyle passes the stroke and
   * shadow through untouched.
   *
   * Keys carry a defaultValue: none of them are in the translation files yet,
   * and without one a missing key renders as the raw key on screen.
   */
  initializeTexts() {
    if (!this.introSpine?.skeleton) return;

    // The reload variant injects NOTHING here — no congratulations. Nothing was
    // just won; the player is being told where they left off. The slot has no
    // attachment of its own, so leaving it empty simply draws nothing.
    const plate = this._plateLines();

    if (!this.isReload) {
      // this._addSlotText(
      this.congratsText = this._addSlotText(
        this._resolveSlot(CONGRATS_SLOTS),
        plate.congrats,
        FONT_STYLES.freeSpinsCongrats,
        111,
      );
    }

    // Both label lines come from ONE sentence per variant — see _plateLines:
    //   intro    CONGRATULATIONS / YOU WON  / n / FREE SPIN(S)
    //   reload                   / YOU HAVE / n / FREE SPIN(S) LEFT
    this.freeSpinsLeadText = this._addSlotText(
      this._resolveSlot(YOU_WON_SLOTS),
      plate.top,
      FONT_STYLES.freeSpinsText,
      // 84,
      LINE_FONT_SIZE,
    );

    this.freeSpinsLabelText = this._addSlotText(
      this._resolveSlot(FREESPINS_SLOTS),
      plate.bottom,
      FONT_STYLES.freeSpinsText,
      // 84,
      LINE_FONT_SIZE,
    );
    this._fitPlateTexts();

    this.initializeValueText();
  }

  /**
   * The spin count, in the value slot belonging to the current level.
   *
   * Each level has its OWN value slot so the artist can colour the number to
   * match the plate. Only one is ever filled; the other two have no attachment
   * and draw nothing. Rebuilt by setTier, because the slot changes with it.
   */
  /**
   * The RELOAD plate's two words, from the shared package's one sentence.
   *
   * ── One string, three slots ──
   *
   * `you_have_freespins_left` is authored as three lines:
   *
   *   {count, plural, one {You have\n{count}\nFree Spin left}
   *                 other {You have\n{count}\nFree Spins left}}
   *
   * and this plate has three places to put them — the top word, the number's
   * own slot, and the bottom word. So the sentence is resolved and split, line
   * 0 goes up top and line 2 goes underneath. Line 1 is the count, which the
   * spine draws itself from `_freeSpinCount`, so it is dropped here.
   *
   * ── Why not the three keys it used to use ──
   *
   * It read `free_spins_left` and `free_spins_left_label` — two invented word
   * fragments that exist in no translation file, so both fell through to their
   * English defaults in every language. Worse, they read FREE SPINS / n / LEFT,
   * which is not the sentence at all, and no fragment can be made plural: a
   * player with one spin was told "1 FREE SPINS LEFT".
   *
   * Reported by the copy owner, 2026-09-21, with this exact string.
   *
   * Uppercased here because the art is caps and the copy is authored in
   * sentence case — the same rule the Buy Bonus titles follow.
   */
  _plateLines() {
    const count = this._freeSpinCount ?? 0;

    // The INTRO sentence carries a fourth line — the congratulation — so the
    // two are read from the end rather than the start: the label is always
    // last, the number always the one before it, and the lead-in the one
    // before that. Indexing from 0 would need a different map per variant.
    //
    //   reload   You have / {count} / Free Spins left
    //   intro    Congratulations! / You won / {count} / Free Spins
    const sentence = this.isReload
      ? this.t("you_have_freespins_left", {
          count,
        })
      : this.t("congrats_you_won_free_spins", {
          count,
        });

    // const lines = String(sentence).split("\n");
    // const label = lines[lines.length - 1] ?? "";
    // const lead = lines[lines.length - 3] ?? "";
    // const congrats = lines.length > 3 ? lines[0] : "";
    // ── Split on the COUNT's line, not on positions from the end ──
    //
    // Reading from the end assumed the label is ONE line. Finnish breaks it in
    // two — "Sinulla on / {count} / Ilmaista pyöräytystä / jäljellä" — so the
    // label came out as "jäljellä" alone and the lead landed on the count's own
    // line, which showed the build-time 0. So the count's line is found, and:
    //
    //   after it         the label, joined onto one line
    //   just before it   the lead
    //   above that       the congratulation (intro only)
    //
    // Trimmed, because zh-CN pads its lines with a trailing space.
    const lines = String(sentence).split("\n").map((l) => l.trim());
    const countAt = lines.indexOf(String(count));
    let congrats;
    let lead;
    let label;
    if (countAt >= 0) {
      label = lines.slice(countAt + 1).join(" ");
      lead = lines[countAt - 1] ?? "";
      congrats = lines.slice(0, Math.max(0, countAt - 1)).join(" ");
    } else {
      // No line is the bare count — keep the old reading.
      label = lines[lines.length - 1] ?? "";
      lead = lines[lines.length - 3] ?? "";
      congrats = lines.length > 3 ? lines[0] : "";
    }

    return {
      congrats: congrats.toUpperCase(),
      top: lead.toUpperCase(),
      bottom: label.toUpperCase(),
    };
  }

  initializeValueText() {
    if (this._valueContainer) {
      // DETACH BEFORE DESTROYING. addSlotObject keeps the container in the
      // spine's own `slotsObject` map, and updateSlotObject walks that map
      // every render — a destroyed container there means a null transform and
      // a dead render loop. Any setTier() that changes level hits this.
      if (this._valueSlotName && this.introSpine?.removeSlotObject) {
        try {
          this.introSpine.removeSlotObject(
            this._valueSlotName,
            this._valueContainer,
          );
        } catch {
          // Already detached with the spine — nothing to do.
        }
      }
      // It is in textContainers too; drop it so cleanup cannot double-destroy.
      if (this.textContainers) {
        this.textContainers = this.textContainers.filter(
          (c) => c !== this._valueContainer,
        );
      }
      this._valueContainer.destroy({ children: true });
      this._valueContainer = null;
      this.freeSpinCountText = null;
    }

    const slotName = LEVEL_VALUE_SLOTS[this.level];
    // The gamble card's own style for this tier — see VALUE_FONT_SCALE.
    // Falls back to the win counter only if a re-export ever drops the key,
    // so a missing style shows the wrong face rather than no number at all.
    const numberStyle =
      FONT_STYLES.gambleLevel?.[this.level] || FONT_STYLES.winCelebrationCounter;
    this.freeSpinCountText = this._addSlotText(
      slotName,
      String(this._freeSpinCount ?? ""),
      numberStyle,
      // Math.round(FS_NUMBER_FONT_SIZE * VALUE_FONT_SCALE),
      NUMBER_FONT_SIZE,
    );
    this._valueContainer = this.freeSpinCountText?.parent || null;
    this._valueSlotName = slotName;
  }

  /**
   * One text, in one slot, fitted to its bone.
   *
   * @returns {PIXI.Text|null} the text object, for later updates.
   */
  _addSlotText(slotName, text, style, fontSize, extra = {}) {
    if (!slotName || !style) return null;
    // ── The spine may not exist yet ──
    //
    // Every other method that touches the skeleton opens with this exact
    // guard — applyLevelSkin, initializeTexts, warmUpIntroSpine — and this
    // one, the primitive they all call, did not.
    //
    // setTier is dispatched from a mount effect while the controller is still
    // being built inside safeInit, which defers construction by a frame. It
    // calls applyLevelSkin, which returned quietly on its guard, and then
    // initializeValueText, which came straight here and threw. React then tore
    // down the whole tree, which is where the cascade of destroy() errors in
    // the console came from — every one of those is the unwind, not a bug.
    //
    // Returning null loses nothing: setTier has ALREADY recorded this.level,
    // and initializeTexts calls initializeValueText itself once the spine is
    // up, so the number is created then with the tier the round actually won.
    if (!this.introSpine?.skeleton) return null;
    const slot = this.introSpine.skeleton.findSlot(slotName);
    if (!slot) return null;

    const container = new PIXI.Container();
    container.name = `${slotName}_text_attached_to_slot`;

    const textObj = new PIXI.Text(
      text,
      new PIXI.TextStyle(scaleStyle(style, fontSize, extra)),
    );
    textObj.resolution = 2;
    textObj.anchor.set(0.5);
    container.addChild(textObj);

    // The bone's length is the artist's statement of how much room there is.
    // const boneWidth = slot.bone?.data?.length || 0;
    // if (boneWidth > 0 && textObj.width > boneWidth) {
    //   container.scale.set(boneWidth / textObj.width);
    // }
    // On the TEXT: spine-pixi writes the bone's scale onto the container every
    // frame, so a fit there never held. No bone length -> LINE_MAX_WIDTH.
    const boneWidth = slot.bone?.data?.length || LINE_MAX_WIDTH;
    // Remembered on the text so _fitPlateTexts can apply it together with the
    // screen cap (3 Orbs of Zeus d6819745, 2026-10-05).
    textObj._boneWidth = boneWidth;
    if (boneWidth > 0 && textObj.width > boneWidth) {
      textObj.scale.set(boneWidth / textObj.width);
    }

    this.introSpine.addSlotObject(slotName, container);

    if (!this.textContainers) this.textContainers = [];
    this.textContainers.push(container);
    return textObj;
  }

  /**
   * Update the free spin count text with the actual value from Redux.
   * Called by the component when showing the dialog.
   */
  updateFreeSpinCount(count) {
    this._freeSpinCount = count;
    if (this.freeSpinCountText) {
      this.freeSpinCountText.text = String(count);
    }
    // The WORD follows the number: "FREE SPIN" at one, "FREE SPINS" at
    // anything else. The count arrives from Redux AFTER the texts are built,
    // so a plural fixed at build time would be wrong exactly when it shows 1 —
    // and that is true of the intro as well as the reload.
    // The LEAD follows it too: Russian's changes with the plural ("остался" /
    // "осталось"), and it was the one word never refreshed.
    const plate = this._plateLines();
    if (this.freeSpinsLabelText) {
      // this.freeSpinsLabelText.text = this._plateLines().bottom;
      this.freeSpinsLabelText.text = plate.bottom;
    }
    if (this.freeSpinsLeadText) {
      this.freeSpinsLeadText.text = plate.top;
    }
    this._fitPlateTexts();
  }

  /**
   * Update button text based on current device (tap vs click)
   */
  updateButtonText() {
    if (!this.buttonText) return;

    this.buttonText.text = this._buttonLabel();
    this._fitTapButton?.(this.dimensions);
  }

  /**
   * Show continue button with fade-in animation
   */
  showButton() {
    // Show PIXI button attached to bone
    if (this.buttonContainer) {
      anime({
        targets: this.buttonContainer,
        alpha: 1,
        duration: 300,
        easing: "easeOutQuad",
      });
    }

  }

  /**
   * Hide continue button with fade-out animation
   */
  hideButton() {
    if (this.buttonContainer) {
      anime({
        targets: this.buttonContainer,
        alpha: 0,
        duration: 300,
        easing: "easeOutQuad",
      });
    }

  }

  /**
   * Start the intro animation sequence
   */
  startSequence() {
    return new Promise((resolve) => {
      if (this.isPlaying || !this.introSpine?.state) {
        resolve();
        return;
      }

      this.isPlaying = true;
      this.isWaitingForClick = false;
      this.overlayRef.current?.fadeInBackground();

      // Reset user interaction guards
      this.isHandlingUserClick = false;
      this.canSkipAnimation = false;

      // Clear any existing timeouts
      if (this.skipProtectionTimeout) {
        this.skipProtectionTimeout.clear();
        this.skipProtectionTimeout = null;
      }
      if (this.debounceTimeout) {
        this.debounceTimeout.clear();
        this.debounceTimeout = null;
      }
      if (this.autoHideTimeout) {
        this.autoHideTimeout.clear();
        this.autoHideTimeout = null;
      }

      // Play free_spin_intro sound when animation appears
      soundManager.play(SOUND_NAME.free_spin_intro);

      // Spine is already positioned correctly at all times (via updateDimensions)
      // Just ensure final positioning check before showing
      if (
        this.introSpine &&
        this.symbolHeight &&
        this.symbolHeight > 0 &&
        this.dimensions
      ) {
        this.applyScaleAndCenter();
      }

      this.introSpine.state.clearTracks();
      this.introSpine.state.clearListeners();
      this.introSpine.state.apply(this.introSpine.skeleton);
      this.introSpine.skeleton.setToSetupPose();

      // SKIP PROTECTION: Block skipping for the first second (matching H&W pattern)
      // Show button after 1 second
      this.skipProtectionTimeout = new TickerTimeout(() => {
        if (this.isPlaying) {
          this.canSkipAnimation = true;
          this.showButton();
          this.isWaitingForClick = true;
          this._armAutoHide();
        }
        // Input unlocks when the spine has finished revealing the tap word,
        // not on a flat timer — see attachTapToContinue.revealMs.
      }, this._revealMs?.(this._resolveAnim(ANIM.IN)) ?? 1000);

      // Play intro_in animation while STILL HIDDEN — lets GPU process new pose
      if (this.introSpine) this.introSpine.autoUpdate = true;
      this.introSpine.state.setAnimation(0, this._resolveAnim(ANIM.IN), false);

      // Show on NEXT FRAME — old warmup pose is gone, new animation starts fresh
      if (this._showRAF) cancelAnimationFrame(this._showRAF);
      this._showRAF = requestAnimationFrame(() => {
        this._showRAF = null;
        if (this.isPlaying && this.introSpine) {
          this.introSpine.alpha = 1;
        }
      });

      // Get intro_in animation duration
      const inDuration = this.getSpineAnimationDuration(this._resolveAnim(ANIM.IN));

      // After intro_in completes, start looping intro_idle
      const inTimeout = new TickerTimeout(() => {
        if (this.isPlaying) {
          if (this.introSpine) this.introSpine.autoUpdate = true;
          this.introSpine.state.setAnimation(0, this._resolveAnim(ANIM.IDLE), true);
          resolve(); // Sequence ready, waiting for user click
        }
      }, inDuration);

      // Store timeout for cleanup
      this.currentTimeout = inTimeout;
    });
  }

  /**
   * Get animation duration from spine data
   */
  getSpineAnimationDuration(animationName) {
    if (
      !this.introSpine ||
      !this.introSpine.skeleton ||
      !this.introSpine.skeleton.data ||
      !this.introSpine.skeleton.data.animations
    ) {
      return 1000; // Default fallback
    }

    try {
      const animation = this.introSpine.skeleton.data.animations.find(
        (anim) => anim.name === animationName,
      );

      if (animation && animation.duration) {
        const durationMs = Math.round(animation.duration * 1000);
        return durationMs;
      }

      return 1000; // Default fallback
    } catch (error) {
      return 1000; // Default fallback
    }
  }

  /**
   * Handle user click to close animation
   * Optimized debouncing - only 1 click processed
   */
  handleUserClick() {
    // Basic validation
    if (!this.isPlaying || !this.introSpine?.state) {
      return false;
    }

    // Block all clicks if debounce timeout exists
    if (this.debounceTimeout) {
      return false;
    }

    // Skip protection: block input for first second of animation
    if (!this.canSkipAnimation) {
      return false;
    }

    // Check if waiting for click
    if (!this.isWaitingForClick) {
      return false;
    }

    // Create debounce timeout to block subsequent clicks
    this.debounceTimeout = new TickerTimeout(() => {
      this.debounceTimeout = null;
    }, 300);

    // Close the animation
    this.playClosingAnimation();

    return true;
  }

  /**
   * Close the popup on the player's behalf if they never tap.
   *
   * GDD §12 gives this popup an auto-hide "after 10s only if the bonus
   * auto-starts". Read literally that condition never fires here — our bonus
   * always waits for the tap, and the wipe into the room is started by
   * playClosingAnimation below. So the qualifier would leave wide open the one
   * case the rule exists for: a player who triggers the bonus and walks away,
   * or an autoplay session left running, stalls on this popup for ever with
   * the round half finished and the UI locked behind it.
   *
   * Applied unconditionally instead. Nothing is taken from a player who IS
   * watching — 10s is several times longer than anyone spends reading four
   * words, and the first tap cancels it.
   *
   * It runs the SAME path a tap does, so the hand-over to the scene wipe and
   * everything after it is identical either way. This is not a second way to
   * close the popup; it is the tap firing itself.
   */
  _armAutoHide() {
    // ── Never on the reload popup ──
    //
    // The timer is there so a bonus that is TRIGGERED and then walked away from
    // cannot strand the round behind a popup. That argument is about a moment
    // the player may have missed; it does not apply to the "FREE SPINS LEFT"
    // card, which is shown because they came BACK. That one is a deliberate
    // check-in and waits for the tap however long it takes.
    //
    // Guarded here rather than at the call site so a second caller cannot
    // reintroduce it by accident.
    if (this.isReload) return;
    // Off by the feature (FEATURE_INTRO.AUTO_CLOSE): only the tap closes it.
    if (!FEATURES_CONFIG.FEATURE_INTRO?.AUTO_CLOSE) return;

    if (this.autoHideTimeout) {
      this.autoHideTimeout.clear();
      this.autoHideTimeout = null;
    }
    this.autoHideTimeout = new TickerTimeout(() => {
      this.autoHideTimeout = null;
      // Anything that closed or advanced the popup in the meantime wins.
      if (!this.isPlaying || !this.isWaitingForClick) return;
      this.playClosingAnimation();
    }, AUTO_HIDE_MS);
  }

  /**
   * Play closing out animation when user clicks to continue
   */
  playClosingAnimation() {
    if (!this.isWaitingForClick || !this.introSpine?.state) return;

    // Tap or timer, whichever came first — the other must find nothing to do.
    if (this.autoHideTimeout) {
      this.autoHideTimeout.clear();
      this.autoHideTimeout = null;
    }

    this.isWaitingForClick = false;
    this.hideButton();

    // Stop intro sound
    soundManager.stop(SOUND_NAME.free_spin_intro);

    // ── And the popup leaving, on the same beat as the `out` clip ──
    //
    // HERE and not in handleUserClick, so the 10s auto-hide gets it too: both
    // routes end up in this method by design — the timer calls the same close
    // the tap does. The two teardown paths (hide/cleanup) are deliberately
    // silent; they are not a close the player watches.
    // No close sound (2026-10-22): the intro opens with its sound and the
    // transition comes on top — nothing closes on its own.
    // soundManager.play(SOUND_NAME.free_spin_intro_close);

    this.introSpine.state.clearListeners();

    const outName = this._resolveAnim(ANIM.OUT);
    if (this.introSpine) this.introSpine.autoUpdate = true;
    this.introSpine.state.setAnimation(0, outName, false);
    this.introSpine.state.addListener({
      complete: (entry) => {
        if (entry.animation.name === outName && this.isPlaying) {
          // ── Hand over to the scene wipe ──
          //
          // Same handover Pot Collection's intro does: the popup finishes its
          // own `out`, then starts the transition and lets it own everything
          // after that. Entering the round is NOT done here — the wipe's
          // `transition_midpoint` event swaps the rooms under full cover, so
          // nothing about the change is ever on screen.
          //
          // NOT setFreeSpinTransitionActive: that is Zeus's flag, and the
          // component that consumed it is no longer mounted in this game.
          //
          // The RELOAD variant does not do this. There is no room to enter —
          // the scene was already restored behind the popup, so a wipe here
          // would cover a board the player is already looking at and re-run an
          // entry that already happened. It just hands the UI back below.
          if (this.dispatch && !this.isReload) {
            this.dispatch(setShowTransitionDialog(true));
          }
          this.finishAnimation();
        }
      },
    });
  }

  /**
   * Finish the animation and clean up
   */
  finishAnimation() {
    if (!this.introSpine || !this.initialized) return;

    this.isPlaying = false;
    this.isWaitingForClick = false;
    this.introSpine.alpha = 0;
    if (this.introSpine) this.introSpine.autoUpdate = false;

    // Cancel pending show RAF
    if (this._showRAF) {
      cancelAnimationFrame(this._showRAF);
      this._showRAF = null;
    }

    // Stop sounds
    soundManager.stop(SOUND_NAME.free_spin_intro);

    // Clear all timeouts
    if (this.currentTimeout) {
      this.currentTimeout.clear();
      this.currentTimeout = null;
    }
    if (this.autoHideTimeout) {
      this.autoHideTimeout.clear();
      this.autoHideTimeout = null;
    }
    if (this.skipProtectionTimeout) {
      this.skipProtectionTimeout.clear();
      this.skipProtectionTimeout = null;
    }
    if (this.debounceTimeout) {
      this.debounceTimeout.clear();
      this.debounceTimeout = null;
    }

    // Reset user interaction guards
    this.isHandlingUserClick = false;
    this.canSkipAnimation = false;

    // Wait for overlay fade out to complete before removing dialog
    this.overlayRef.current?.fadeOutBackground(() => {
      if (!this.dispatch) return;
      if (this.isReload) {
        // Everything the reload popup was holding, handed back together — the
        // same three dispatches Pot Collection's reload dialog ends on. The
        // free-spin loop is gated on both flags, so the round resumes here and
        // not before.
        this.dispatch(setShowFreeSpinsReload(false));
        this.dispatch(setCriticalAnimationPlaying(false));
        this.dispatch(setUserInterfaceDisabled(false));
        // Releases the win presentation both controllers were holding. Last of
        // the four on purpose: the screen is clear before anything celebrates.
        this.dispatch(setFreeSpinsReloadPopupPending(false));
        return;
      }
      this.dispatch(setShowFreeSpinIntro(false));
    });
  }

  /**
   * Cleanup resources
   */
  cleanup() {
    // Cancel pending show RAF
    if (this._showRAF) {
      cancelAnimationFrame(this._showRAF);
      this._showRAF = null;
    }

    // Stop sounds
    soundManager.stop(SOUND_NAME.free_spin_intro);

    // Clear all timeouts
    if (this.currentTimeout) {
      this.currentTimeout.clear();
      this.currentTimeout = null;
    }
    if (this.autoHideTimeout) {
      this.autoHideTimeout.clear();
      this.autoHideTimeout = null;
    }
    if (this.skipProtectionTimeout) {
      this.skipProtectionTimeout.clear();
      this.skipProtectionTimeout = null;
    }
    if (this.debounceTimeout) {
      this.debounceTimeout.clear();
      this.debounceTimeout = null;
    }

    // Remove slot objects from spine
    if (this.introSpine?.removeSlotObjects) {
      this.introSpine.removeSlotObjects();
    }

    // Cleanup button
    if (this.buttonContainer) {
      this.buttonContainer.destroy({ children: true });
      this.buttonContainer = null;
      this.buttonText = null;
    }

    // Cleanup text containers
    if (this.textContainers) {
      for (const container of this.textContainers) {
        container.destroy({ children: true });
      }
      this.textContainers = null;
    }

    if (this.introSpine) {
      this.introSpine.state?.clearListeners();
      this.introSpine.state?.clearTracks();
      if (this.introSpine.state) this.introSpine.state.timeScale = 1.0;

      if (this.container?.children.includes(this.introSpine)) {
        this.container.removeChild(this.introSpine);
      }
      this.introSpine.destroy();
      this.introSpine = null;
    }

    this.initialized = false;
  }
}

export default FreeSpinIntroDialogController;
